/**
 * Mail-tekst opschonen voor notities (16 sep 2026).
 * De Outlook-tekstbody die in notities belandt ("Mail van:", "Uit e-mailgesprek") bevat
 * afbeeldings-URL's tussen haken, tel:-links, handtekeningregels en de hele geciteerde
 * historie ("Van: … Verzonden: …"). Dat maakt de Notes-pagina onleesbaar. Hier:
 * - schoonMailTekst: ruis weg, losse bullets hersteld
 * - splitsMail: eigen tekst / handtekening / eerdere berichten (die klapt de UI in)
 * - splitsGesprek: idem per bericht voor een "Uit e-mailgesprek"-notitie
 */
const AFBEELDING = /\[(?:cid:[^\]]*|https?:\/\/[^\]\s]+\.(?:png|jpe?g|gif|svg|webp|bmp)(?:\?[^\]\s]*)?)\]/gi;
const TEL = /\btel:\+?\d[\d\s()-]{5,}/gi;
const CITAAT_MARKERS = [
  /^-{2,}\s*(oorspronkelijk bericht|original message|forwarded message)/im,
  /^\s*(van|from):\s*.+$/im,
  /^\s*op .{5,80}\s+schreef\s+.{2,80}:\s*$/im,
  /^\s*on .{5,80}\s+wrote:\s*$/im,
];
// Korte afsluiters als "Thx!" blijven zichtbaar; alleen de echte handtekeningkop klapt in.
const GROET = /^\s*(met vriendelijke groet(en)?|vriendelijke groet(en)?|hartelijke groet(en)?|groet(en|jes)?|gr\.? [A-Z]|kind regards|best regards|regards|cheers)\b/i;

export function isMailNotitie(tekst) {
  return /^\*\*Mail van:\*\*|— Uit e-mailgesprek:/m.test(String(tekst || ''));
}

export function schoonMailTekst(tekst) {
  let s = String(tekst || '').replace(/\r/g, '');
  s = s.replace(AFBEELDING, ' ').replace(TEL, ' ');
  s = s.replace(/^\s*biografie\s*https?:\/\/\S+.*$/gim, '');                 // "Biografiehttps://fullframe.nl/team/…"
  s = s.replace(/^\s*(https?:\/\/(?:www\.)?(?:fullframe\.nl|only\.nl|app\.estampida\.nl)\S*[ \t]*)+$/gim, '');
  s = s.replace(/^[ \t]*[•*·][ \t]*\n(?=\S)/gm, '• ');                        // losse bullet op eigen regel
  s = s.replace(/(^|\s)\*\s+(?=[A-Z0-9])/g, '$1\n• ');                         // inline " * " opsommingen
  s = s.split('\n').map((l) => l.replace(/[ \t]{2,}/g, ' ').trimEnd()).join('\n');
  return s.replace(/\n{3,}/g, '\n\n').trim();
}

// Outlook (vooral iOS) plakt de kop van een doorgestuurde/beantwoorde mail achter
// een rij underscores op één regel: "____ Van: X Verzonden: … Aan: … Onderwerp: …".
// Zet die kopregels op eigen regels, anders ziet niemand waar het oude bericht begint.
const KOPWOORDEN = '(?:Van|From|Verzonden|Sent|Aan|To|CC|Cc|Onderwerp|Subject|Datum|Date)';
export function normaliseerKoppen(tekst) {
  let s = String(tekst || '');
  s = s.replace(/_{3,}\s*/g, '\n');
  s = s.replace(/Verzonden vanuit Outlook[^\n]*?(?=\s*(?:Van|From):)/gi, '');
  s = s.replace(new RegExp(`(?<!\\n)[ \\t]+(?=${KOPWOORDEN}:\\s)`, 'g'), '\n');
  return s;
}

const KOP_RE = /^(?:Van|From):\s*(.+?)\n(?:(?:Verzonden|Sent|Datum|Date):\s*(.+?)\n)?(?:(?:Aan|To|CC|Cc):[^\n]*\n)*(?:(?:Onderwerp|Subject):\s*(.+?)\n)?/i;

/**
 * Doorgestuurde mail: de eigen tekst is meestal één zin ("Hierbij alvast info voor
 * Jef"), de inhoud staat in het doorgestuurde bericht. Dan hoort dát open te staan,
 * niet ingeklapt als "eerder bericht". Herkenning: onderwerp begint met FW/Fwd/Doorst,
 * óf de eigen tekst is kort en het citaat begint met een Van:-kop.
 */
function parseDoorgestuurd(citaat, eigen, onderwerp) {
  if (!citaat) return null;
  const m = KOP_RE.exec(citaat);
  if (!m) return null;
  // Forward = onderwerp zegt het, óf een korte intro met doorstuur-taal ("hierbij", "zie
  // onder", "info voor"). Een kort antwoord ("Thx!") op een lange thread is géén forward.
  const forwardTaal = /hierbij|bij deze|zie (?:hier)?onder|onderstaand|fyi|ter info|doorgestuurd|door te sturen|info voor|forward/i;
  const isForward = /^\s*(fw|fwd|doorst|tr)\b/i.test(onderwerp || '') || ((eigen || '').length < 250 && forwardTaal.test(eigen || ''));
  if (!isForward) return null;
  const body = citaat.slice(m[0].length);
  // Het doorgestuurde bericht kan zelf weer historie bevatten: daar knippen we opnieuw.
  let knip = body.length;
  for (const re of CITAAT_MARKERS) {
    const mm = re.exec(body);
    if (mm && mm.index > 40 && mm.index < knip) knip = mm.index;
  }
  return { van: (m[1] || '').trim(), datum: (m[2] || '').trim(), onderwerp: (m[3] || '').trim(),
    tekst: body.slice(0, knip).trim(), citaat: body.slice(knip).trim() };
}

export function splitsMail(tekst, opties = {}) {
  const s = normaliseerKoppen(schoonMailTekst(tekst));
  let knip = s.length;
  for (const re of CITAAT_MARKERS) {
    const m = re.exec(s);
    if (m && m.index > 40 && m.index < knip) knip = m.index;
  }
  let eigen = s.slice(0, knip).trim();
  const citaat = s.slice(knip).trim();
  let handtekening = '';
  const regels = eigen.split('\n');
  // Van onderaf: de hoogste groetregel binnen de laatste 14 regels wint, zolang het
  // stuk eronder een handtekening kan zijn (< 700 tekens).
  let knipRegel = -1;
  for (let i = regels.length - 1; i >= Math.max(0, regels.length - 14); i -= 1) {
    if (GROET.test(regels[i]) && regels.slice(i).join('\n').length < 700) knipRegel = i;
  }
  if (knipRegel >= 0) {
    handtekening = regels.slice(knipRegel).join('\n').trim();
    eigen = regels.slice(0, knipRegel).join('\n').trim();
  }
  const doorgestuurd = opties.geenForward ? null : parseDoorgestuurd(citaat, eigen, opties.onderwerp);
  if (doorgestuurd) {
    // Het doorgestuurde bericht heeft zijn eigen handtekening; die halen we er ook af.
    const binnen = splitsMail(doorgestuurd.tekst, { geenForward: true });
    return { eigen, handtekening, citaat: doorgestuurd.citaat,
      doorgestuurd: { ...doorgestuurd, tekst: binnen.eigen || doorgestuurd.tekst, handtekening: binnen.handtekening } };
  }
  return { eigen, handtekening, citaat, doorgestuurd: null };
}

/** "— Bijlage: naam —" blokken (mailBijlagen.js) uit een tekst halen, zodat ze apart getoond worden. */
export function haalBijlagen(tekst) {
  const delen = String(tekst || '').split(/\n?— Bijlage: (.+?) —\n/);
  if (delen.length < 3) return { rest: String(tekst || ''), bijlagen: [] };
  const bijlagen = [];
  for (let i = 1; i < delen.length; i += 2) bijlagen.push({ naam: delen[i].replace(/ \(bewaard bij Documenten\)$/, ''), inDocumenten: / \(bewaard bij Documenten\)$/.test(delen[i]), tekst: (delen[i + 1] || '').trim() });
  return { rest: delen[0].trim(), bijlagen };
}

/** "Uit e-mailgesprek"-notities: kop + één blok per bericht ("[n] · datum · van X"). */
export function splitsGesprek(tekst) {
  const s = String(tekst || '').replace(/\r/g, '');
  const kopMatch = s.match(/^—\s*Uit e-mailgesprek:[^\n]*—\s*/m);
  const kop = kopMatch ? kopMatch[0].replace(/—/g, '').trim() : '';
  const rest = kopMatch ? s.slice(kopMatch.index + kopMatch[0].length) : s;
  const stukken = rest.split(/(?=\[\d+\]\s*·\s*\d{4}-\d{2}-\d{2})/g).map((x) => x.trim()).filter(Boolean);
  const berichten = (stukken.length ? stukken : [rest]).map((stuk) => {
    const m = stuk.match(/^\[(\d+)\]\s*·\s*(\d{4}-\d{2}-\d{2})\s*·\s*van\s+([^\n]{1,80}?)(?=\s{2,}|\n|$)/);
    const kopregel = m ? `${m[2]} · ${m[3].trim()}` : '';
    const body = m ? stuk.slice(m[0].length) : stuk;
    const { rest, bijlagen } = haalBijlagen(body);
    return { kopregel, ...splitsMail(rest), bijlagen };
  });
  return { kop, berichten };
}
